import { useCallback, useState } from 'react';
import { LayoutChangeEvent, StyleSheet } from 'react-native';
import { Input } from '../Input';
import { InputProps } from '../types';

// based on: https://github.com/necolas/react-native-web/issues/795#issuecomment-1738144010
const adjustTextInputSize: (
	maxHeight?: number,
	originalHeight?: number,
) => NonNullable<InputProps['onChange']> =
	(maxHeight = 0, originalHeight = 0) =>
	(event) => {
		const element = (event?.target || event?.nativeEvent?.target) as unknown as HTMLElement | null;
		if (!element) {
			return;
		}
		element.style.height = `${originalHeight}px`;
		const newHeightCandidate = element.offsetHeight - element.clientHeight + element.scrollHeight;

		const newHeight = maxHeight > 0 ? Math.min(newHeightCandidate, maxHeight) : newHeightCandidate;
		element.style.height = `${newHeight}px`;
	};

export const AutoGrowInput = ({ textStyle, onBlur, ...props }: InputProps) => {
	const [originalHeight, setOriginalHeight] = useState<number>();
	const maxHeight = StyleSheet.flatten(textStyle).maxHeight;

	const handleBlur: NonNullable<InputProps['onBlur']> = useCallback(
		(event) => {
			onBlur?.(event);
			/**
			 * This is the workaround for resetting input size after being submitted.
			 * 1. In React Native Web `onSubmitEditing` is invoked only when `blurOnSubmit={true}`.
			 * 		This behavior means that the onBlur event is triggered, but not onChange or onContentSizeChange..
			 * 2. Timeout is to let UI update when submitting with external button and input is resetting.
			 */
			setTimeout(() => adjustTextInputSize(originalHeight, originalHeight)(event), 100);
		},
		[onBlur, originalHeight],
	);

	const saveOriginalHeight = useCallback(
		({ nativeEvent }: LayoutChangeEvent) => {
			if (originalHeight !== undefined) {
				return;
			}
			setOriginalHeight(nativeEvent.layout.height);
		},
		[originalHeight],
	);

	return (
		<Input
			{...props}
			onLayout={saveOriginalHeight}
			textStyle={textStyle}
			onChange={adjustTextInputSize(
				typeof maxHeight === 'number' ? maxHeight : undefined,
				originalHeight,
			)}
			onBlur={handleBlur}
		/>
	);
};
